import {ParamComponent} from 'panel';

import {Permission} from 'common/report/constants';
import {excludeDemos} from 'common/report/util';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import type {NoExtension, Section} from 'common/components/items-menu/types';
import type {IronContextRegistry} from 'common/report/context-registry';
import type {CurrentProject, Project} from 'common/report/queries/app-api/project-metadata-client';
import type {Organization} from 'common/report/queries/app-api/user-metadata-client';

import {template} from './template';
import {BoardMoveSetting} from './types';

export const WRITEABLE_ORG_PERMISSIONS = [Permission.WriteTypedBookmarks, Permission.WriteDashboards];

enum SortingMethod {
  ALPHA_WITH_CURRENT_FIRST = 1,
}

interface SortableEntity {
  name: string;
  id: number;
}

type EntitySortingFunction = (a: SortableEntity, b: SortableEntity) => number;
const SORTING_FUNCTION_DISPATCH_TABLE: Map<SortingMethod, (id: number) => EntitySortingFunction> = new Map([
  [
    SortingMethod.ALPHA_WITH_CURRENT_FIRST,
    (id) => {
      return (a, b) => {
        if (a.id === id) {
          return -1;
        } else if (b.id === id) {
          return 1;
        }
        return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
      };
    },
  ],
]);

/**
 * This type indicates a Map where the keys (organizations) are in sorted order, so that map.keys() produces the
 * correct ordering, and each value (a list of projects) is correctly ordered as well. What "correct" is depends
 * on which SortingMethod is used to create the SortedOrgMap.
 */
type SortedOrgMap = Map<Organization, Project[]>;

// For local code clarity only; do not export.
type ProjectId = number;

export class TargetProjectSelectorMenu extends ParamComponent<EmptyObj, EmptyObj, IronContextRegistry> {
  get config() {
    return {
      template,
      contexts: [`currentProject` as const, `currentUser` as const, `projects` as const, `urlManager` as const],
      defaultState: {},
      helpers: {
        projectListSections: () => this.buildProjectListSections(SortingMethod.ALPHA_WITH_CURRENT_FIRST),
      },
    };
  }

  /**
   * The status of the given project's move-between-boards setting.
   */
  private getMoveBetweenBoardsSetting(_: CurrentProject): BoardMoveSetting {
    // While we're developing, use the value from the query-string parameter.
    // TODO(CrossProjectCopy) Get the value from the passed parameter instead.
    return (
      BoardMoveSetting[
        BoardMoveSetting[parseInt(this.getContext(`urlManager`).getQueryStringParam(`x_project_perms`), 10)]
      ] ?? BoardMoveSetting.ALLOWED_WITHIN_ORG
    );
  }

  /**
   * Gets a list of all the organizations the user can write to.
   */
  private getWriteableOrganizations(): Organization[] {
    const allUserOrgs = Object.values(this.getContext(`currentUser`).get().value?.organizations || {});
    return excludeDemos(allUserOrgs).filter((org) => {
      return WRITEABLE_ORG_PERMISSIONS.every((perm) => org.permissions.includes(perm));
    });
  }

  /**
   * Given the list of organizations, creates a mapping between each organization and the projects from
   * the given list of projects that belong to it.
   */
  private bucketProjectsByOrg(orgs: Organization[], projects: Project[]): Map<Organization, Project[]> {
    const buckets: Map<Organization, Project[]> = new Map();
    orgs.forEach((org) =>
      buckets.set(
        org,
        projects.filter((proj) => proj.organizationId === org.id),
      ),
    );
    return buckets;
  }

  /**
   * Sorts the given orgToProjectsMap using the given methodology. The result is a map whose keys are in sorted order and whose
   * values are arrays that are also each in sorted order.
   */
  private sortMenu(
    orgToProjectsMap: Map<Organization, Project[]>,
    methodology: SortingMethod,
    currentProject: Project,
  ): SortedOrgMap {
    const sortingFunctionBuilder = SORTING_FUNCTION_DISPATCH_TABLE.get(methodology);
    const organizationSortingFunction = sortingFunctionBuilder
      ? sortingFunctionBuilder(currentProject.organizationId)
      : undefined;
    const sortedOrganizations = [...orgToProjectsMap.keys()].sort(organizationSortingFunction);

    const sortedOrgMap: SortedOrgMap = new Map();
    const projectSortingFunction = sortingFunctionBuilder ? sortingFunctionBuilder(currentProject.id) : undefined;
    for (const organization of sortedOrganizations) {
      const sortedProjects = orgToProjectsMap.get(organization)?.sort(projectSortingFunction) || [];
      sortedOrgMap.set(organization, sortedProjects);
    }
    return sortedOrgMap;
  }

  private convertToSectionConfiguration(
    orgToProjectsMap: SortedOrgMap,
    currentProject: CurrentProject,
  ): Array<Section<ProjectId, NoExtension>> {
    const setting = this.getMoveBetweenBoardsSetting(currentProject);
    if (setting === BoardMoveSetting.NOT_ALLOWED) {
      return [];
    }

    const sections = [];
    for (const org of orgToProjectsMap.keys()) {
      if (setting === BoardMoveSetting.ALLOWED_WITHIN_ORG && org.id !== currentProject.organizationId) {
        continue;
      }

      if ((orgToProjectsMap.get(org) || []).length === 0) {
        continue;
      }

      sections.push({
        label: orgToProjectsMap.size === 1 || setting === BoardMoveSetting.ALLOWED_WITHIN_ORG ? `` : org.name,
        items: (orgToProjectsMap.get(org) || []).map((project) => {
          return {
            label: project.name,
            sideLabel: project.id === currentProject.id ? `This Project` : undefined,
            value: project.id,
          };
        }),
      });
    }
    return sections;
  }

  /**
   * Constructs the organization/project items configuration to display in the drop down. The items are ordered by
   * the given ordering method.
   */
  private buildProjectListSections(sortingMethodology: SortingMethod): Array<Section<ProjectId, NoExtension>> {
    const currentProject = this.getContext(`currentProject`).get().value;
    const writeableOrgs = this.getWriteableOrganizations();
    const projects = excludeDemos(this.getContext(`projects`).getAllValues());
    if (!currentProject || writeableOrgs.length === 0 || projects.length === 0) {
      return [];
    }

    const orgToProjectsMap = this.bucketProjectsByOrg(writeableOrgs, projects);
    const sortedOrgMap = this.sortMenu(orgToProjectsMap, sortingMethodology, currentProject);
    return this.convertToSectionConfiguration(sortedOrgMap, currentProject);
  }

  connectedCallback() {
    super.connectedCallback();
  }
}

export const TargetProjectSelectorMenuEl = defineAndMakeJsxFactory(
  `mp-target-project-selector-menu`,
  TargetProjectSelectorMenu,
);
export const TargetProjectSelectorMenuReact = panel2react(`mp-target-project-selector-menu`, TargetProjectSelectorMenu);
